import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { clientDisplayName, clientsMissing, CoverageKey, IAgencyDigest, notCountedInCoverage } from '@innerhive-internal/innerhive-common';
import { BRAND, CHART_INK } from './agencyChartTheme';
import { PersonAvatar } from '../../avatar/Avatars';
import { IAvatarIdentity } from '../../avatar/IAvatarIdentity';

/** How many names a meter shows before it stops listing and counts the rest. */
const MAX_NAMED_GAPS = 12;

const useStyles = createUseStyles({
    meters: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 10,
        marginTop: 4
    },

    meter: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 6,
        width: '100%',
        padding: '9px 10px',
        borderRadius: 10,
        border: '1px solid transparent',
        background: 'none',
        font: 'inherit',
        textAlign: 'left' as const,
        cursor: 'pointer',

        '&:hover': {
            background: BRAND.tealWash
        },

        '@media print': {
            breakInside: 'avoid'
        }
    },

    meterOn: {
        borderColor: BRAND.tealEdge,
        background: BRAND.tealWash,

        '&:hover': {
            background: BRAND.tealWash
        }
    },

    meterHead: {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 10,
        fontSize: 13.5,
        color: BRAND.deepTeal,
        fontWeight: 500
    },

    meterCount: {
        fontSize: 12.5,
        fontWeight: 400,
        color: CHART_INK.label,
        fontVariantNumeric: 'tabular-nums'
    },

    track: {
        position: 'relative',
        height: 10,
        borderRadius: 5,
        background: 'var(--ion-color-step-100)',
        overflow: 'hidden'
    },

    fill: {
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        borderRadius: 5,
        background: BRAND.deepTeal
    },

    gaps: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: 8,
        marginTop: 4
    },

    gapsNone: {
        marginTop: 4,
        fontSize: 12.5,
        color: CHART_INK.label
    },

    gapChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: '3px 11px 3px 3px',
        borderRadius: 20,
        background: 'var(--ion-background-color)',
        border: `1px solid ${BRAND.tealEdge}`,
        fontSize: 12.5,
        color: CHART_INK.strongLabel
    },

    gapMore: {
        alignSelf: 'center',
        fontSize: 12.5,
        color: CHART_INK.label
    },

    unreadable: {
        marginTop: 4,
        fontSize: 12.5,
        lineHeight: 1.5,
        color: CHART_INK.label
    }
});

/**
 * One coverage line, and the clients behind it once it is opened.
 *
 * A percentage tells an agency how they are doing and gives them nothing to do about it. The names
 * are the actionable half, so the meter opens into them rather than sending the reader to hunt
 * through the client list working out which records are short.
 */
interface IClientFaceProps {
    /** How each client is drawn, with the audited link to a hosted photo. */
    clientAvatar: (careMapId: string) => IAvatarIdentity | undefined;
}

const Meter: FC<IClientFaceProps & {
    label: string;
    count: number;
    total: number;
    missing: { careMapId: string; clientName: string }[];
    isOpen: boolean;
    onToggle: () => void;
}> = ({ label, count, total, missing, isOpen, clientAvatar, onToggle }) => {
    const classes = useStyles();
    const share = total === 0 ? 0 : Math.round((count / total) * 100);
    const named = missing.slice(0, MAX_NAMED_GAPS);

    return (
        <button
            type="button"
            className={`${classes.meter} ${isOpen ? classes.meterOn : ''}`}
            data-id="coverage-meter"
            aria-expanded={isOpen}
            onClick={onToggle}
        >
            <span className={classes.meterHead}>
                <span>{label}</span>
                <span className={classes.meterCount}>{count} of {total} ({share}%)</span>
            </span>
            <span className={classes.track}>
                <span className={classes.fill} style={{ width: `${share}%` }} />
            </span>

            {isOpen && (missing.length === 0
                ? <span className={classes.gapsNone}>Every client I can read has this on file.</span>
                : (
                    <span className={classes.gaps} data-id="coverage-gap-list">
                        {named.map(client => (
                            <span key={client.careMapId} className={classes.gapChip}>
                                <PersonAvatar size={22} identity={clientAvatar(client.careMapId)} name={client.clientName} />
                                <span className="mp-mask">{clientDisplayName(client.clientName)}</span>
                            </span>
                        ))}
                        {missing.length > named.length && (
                            <span className={classes.gapMore}>and {missing.length - named.length} more</span>
                        )}
                    </span>
                ))}
        </button>
    );
};

/**
 * What an agency can evidence today, as a share of the caseload with each thing on file.
 *
 * Coverage needs no finding history to be true, so it is real for a caseload of any age. Opening a
 * meter lists names rather than narrowing the rest of the tab.
 */
export const CoverageMeters: FC<IClientFaceProps & {
    digest: IAgencyDigest;
    openMeter?: CoverageKey;
    onToggleMeter: (key: CoverageKey) => void;
}> = ({ digest, openMeter, clientAvatar, onToggleMeter }) => {
    const classes = useStyles();
    const { coverage } = digest;
    const notCounted = notCountedInCoverage(digest);

    const meters: { key: CoverageKey; label: string; count: number }[] = [
        { key: 'codeStatus', label: 'Code Status Confirmed', count: coverage.codeStatusConfirmed },
        { key: 'insurance', label: 'Insurance on File', count: coverage.insuranceOnFile },
        { key: 'identity', label: 'Identity Document on File', count: coverage.identityOnFile }
    ];

    return (
        <div className={classes.meters} data-id="coverage-meters">
            {meters.map(({ key, label, count }) => (
                <Meter
                    key={key}
                    label={label}
                    count={count}
                    total={coverage.clientCount}
                    missing={clientsMissing(digest, key)}
                    isOpen={openMeter === key}
                    clientAvatar={clientAvatar}
                    onToggle={() => onToggleMeter(key)}
                />
            ))}

            {notCounted > 0 && (
                <div className={classes.unreadable} data-id="coverage-unreadable-notice">
                    {notCounted} client{notCounted === 1 ? ' is' : 's are'} not counted above:
                    what is on file for them could not be read.
                    {/* Only clients that could not be read reach that panel, so the pointer holds only
                        when every client counted here is one of them. Counted through coverage
                        alone they are on no list, and a reader sent to find a name that is not
                        there is worse off than one given no pointer. */}
                    {digest.unreadableCount === notCounted
                        && ' The panel at the top of this tab names them and says why for each.'}
                </div>
            )}
        </div>
    );
};
